import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Home, BarChart3, Heart, Target } from 'lucide-react';

// Import your individual tab components
import HEPTExecutiveSummary from './HEPT_ExecSummary';
import AwarenessKnowledgeTab from './HEPT_Awareness_Knowledge';
import PersonalHealthTab from './HEPT_PersonalHealth';
import BehavioralIntentTab from './HEPT_BehavioralIntent';

const HEPTDashboard = () => {
  const [activeTab, setActiveTab] = useState('executive');

  const tabs = [
    {
      id: 'executive',
      title: 'Executive Summary',
      icon: Home,
      component: HEPTExecutiveSummary,
      description: 'Strategic Overview'
    },
    {
      id: 'awareness',
      title: 'Awareness & Knowledge',
      icon: BarChart3,
      component: AwarenessKnowledgeTab,
      description: 'HPV/Cervical Cancer Awareness Analysis'
    },
    {
      id: 'health',
      title: 'Personal Health',
      icon: Heart,
      component: PersonalHealthTab,
      description: 'Healthcare Access & Experiences'
    },
    {
      id: 'intent',
      title: 'Behavioral Intent',
      icon: Target,
      component: BehavioralIntentTab,
      description: 'Vaccination Readiness Analysis'
    }
  ];

  const currentTabIndex = tabs.findIndex(tab => tab.id === activeTab);
  const ActiveComponent = tabs[currentTabIndex].component;

  const navigateTab = (direction) => {
    const newIndex = direction === 'next' 
      ? Math.min(currentTabIndex + 1, tabs.length - 1)
      : Math.max(currentTabIndex - 1, 0);
    setActiveTab(tabs[newIndex].id);
  };

  const handleExecutiveNavigation = (tabId) => {
    setActiveTab(tabId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-xl font-bold text-gray-900">MSD HEPT Nigeria</h1>
                <p className="text-xs text-gray-500">Market Entry Strategy Dashboard</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Lagos & Ogun State • 798,000 Target Population
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  <div className="text-left">
                    <div>{tab.title}</div>
                    <div className="text-xs opacity-75">{tab.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm min-h-[800px]">
          {activeTab === 'executive' ? (
            <div className="relative">
              <ActiveComponent onNavigate={handleExecutiveNavigation} />
              
              {/* Navigation buttons from executive summary */}
              <div className="absolute bottom-6 right-6">
                <div className="bg-blue-600 text-white p-4 rounded-lg shadow-lg">
                  <h3 className="font-semibold mb-2">Explore Detailed Analysis</h3>
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => handleExecutiveNavigation('awareness')}
                      className="text-left px-3 py-2 bg-blue-500 hover:bg-blue-400 rounded text-sm transition-colors"
                    >
                      → Awareness Analysis
                    </button>
                    <button
                      onClick={() => handleExecutiveNavigation('health')}
                      className="text-left px-3 py-2 bg-blue-500 hover:bg-blue-400 rounded text-sm transition-colors"
                    >
                      → Healthcare Experience
                    </button>
                    <button
                      onClick={() => handleExecutiveNavigation('intent')}
                      className="text-left px-3 py-2 bg-blue-500 hover:bg-blue-400 rounded text-sm transition-colors"
                    >
                      → Vaccination Intent
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ActiveComponent />
          )}
        </div>

        {/* Navigation Controls */}
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={() => navigateTab('prev')}
            disabled={currentTabIndex === 0}
            className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
              currentTabIndex === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm border'
            }`}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </button>

          <div className="flex items-center space-x-2">
            {tabs.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === currentTabIndex ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <button
            onClick={() => navigateTab('next')}
            disabled={currentTabIndex === tabs.length - 1}
            className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
              currentTabIndex === tabs.length - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm border'
            }`}
          >
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-semibold mb-2">MSD HEPT Nigeria</h3>
              <p className="text-gray-300 text-sm">
                Human Papillomavirus and Cervical Cancer Prevention Technology Market Entry Strategy
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Study Scope</h3>
              <p className="text-gray-300 text-sm">
                2,845 survey respondents across Lagos & Ogun State urban populations, ages 15-45
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Methodology</h3>
              <p className="text-gray-300 text-sm">
                Population-representative analysis using MRP adjustments for demographic accuracy
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HEPTDashboard;
